import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useLoadScript,
  GoogleMap,
  MarkerClusterer,
} from "@react-google-maps/api";
import { observer } from "mobx-react-lite";
import { useStores } from "index";
import { MapMarkers, isSystemCentered } from "./components/utils";
import { MenuTypes } from "./components/enums";
import {
  center,
  containerStyle,
  getMapOptions,
  libraries,
  markerTypes,
} from "./components/constants";

// Components
import ChangeMarkers from "./components/widgets/ChangeMarkers";
import WorldZoom from "./components/widgets/WorldZoom";
import LocateUser from "./components/widgets/LocateUser";
import UpdateTimer from "./components/widgets/UpdateTimer";
import ToolBar from "./components/toolbar/ToolBar";
import clusterIconGenerator from "./components/icons/ClusterIconGenerator";
import { ISystemData } from "./components/interfaces";
import { AppSectionErrorBoundary } from "components/ui/AppSectionErrorBoundary";

export const Map: React.FC<any> = observer((props) => {
  const { systemStore: ss } = useStores();

  const [selectedMarkerId, setSelectedMarkerId] = useState<
    ISystemData["id"] | null | undefined
  >(null);
  const [categorySelected, setCategorySelected] = useState<MenuTypes>(
    MenuTypes.All
  );
  const mapRef: any = useRef();
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [isSwitchingCategory, setIsSwitchingCategory] =
    useState<boolean>(false);

  const lastSelectedMenuSystemRef = useRef<HTMLDivElement | null>(null);
  const [clustererStyles, setClustererStyles] = useState<any>([]);

  const googleApiUrl = useMemo(() => ss.googleApiUrl, []);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleApiUrl,
    libraries,
  });

  const filteredSystems = useMemo(
    () => ss.filteredSystems,
    [ss.filteredSystems]
  );

  const selectedMarkerData = useMemo(
    () => filteredSystems.find((s) => s.id === selectedMarkerId),
    [selectedMarkerId]
  );

  // ===========================================================================
  const onMapLoad = useCallback((map: google.maps.Map) => {
    ss.setIsLoading(true);
    mapRef.current = map;

    let retry = 0;

    google.maps.event.addListener(map, "idle", () => {
      const markersAreRendered = checkIfMarkersAreRendered();

      if (!markersAreRendered) {
        setTimeout(() => {
          map.panBy(0, 1);
          setTimeout(() => {
            map.panBy(0, -1);
          }, 50);
        }, 500);
        retry++;
        console.log("🚀 ~ retry rendering markers: ", retry);
      } else {
        ss.setIsLoading(false);
      }
    });
  }, []);

  const checkIfMarkersAreRendered = () => {
    const mapBounds = mapRef.current.getBounds();
    const mapCenter = mapRef.current.getCenter();
    const markersAreRendered = ss.systems.some((marker) => {
      const markerPosition = new google.maps.LatLng(
        marker.position.lat,
        marker.position.lng
      );

      return (
        mapBounds.contains(markerPosition) &&
        mapCenter.lat() !== 0 &&
        mapCenter.lng() !==
          0 /* This is to prevent the map from panning to the middle of the ocean */
      );
    });

    return markersAreRendered;
  };

  //  ===========================================================================

  const panTo = useCallback(({ lat, lng, zoom }) => {
    if (typeof lat === "number" && typeof lat === "number") {
      mapRef.current.setZoom(zoom);
      mapRef.current.panTo({ lat, lng });
    }
  }, []);

  const markerOnClickHandler = useCallback((markerData: ISystemData) => {
    // If the clicked marker is in the center, show the menu if not already shown
    if (
      isSystemCentered(
        markerData,
        mapRef.current.center.lat(),
        mapRef.current.center.lng()
      ) &&
      !showMenu
    )
      setShowMenu(true);

    // Focus map on the clicked marker
    panTo({
      lat: markerData.position.lat,
      lng: markerData.position.lng,
      zoom: Math.max(17, mapRef.current.zoom),
    });

    // Select the clicked marker as the primary selected marker
    setSelectedMarkerId(markerData.id);

    // Scroll to the item in the menu list with the unit-selected class
    if (lastSelectedMenuSystemRef.current) {
      lastSelectedMenuSystemRef.current.scrollIntoView();
    }
  }, []);

  useEffect(() => {
    if (isSwitchingCategory) {
      setIsSwitchingCategory(false);
    }
  }, [isSwitchingCategory]);

  const toggleCategory = (val: MenuTypes) => {
    setCategorySelected((prevState) => {
      if (prevState !== val) {
        setIsSwitchingCategory(true);
      }

      return val;
    });
  };

  const generateClusterStyles = (markerClusterer) => {
    if (!isLoaded) return;
    let styles: any[] = [];

    markerClusterer.clusters.forEach((cluster: any) => {
      if (!cluster.markers) return;
      const clusterMarkerData: google.maps.Marker[] = cluster.markers
        .filter((m: google.maps.Marker) => m.get("markerData"))
        .map((m: google.maps.Marker) => m.get("markerData"));

      const icon = clusterIconGenerator(clusterMarkerData, markerTypes, 50);

      styles.push(...icon);
      return icon;
    });

    setClustererStyles(styles);
  };

  const clusterCalculator = useCallback(
    (markers: google.maps.Marker[], numOfStyles: number) => {
      const clusterMarkerData: google.maps.Marker[] = markers
        .filter((m: google.maps.Marker) => m.get("markerData"))
        .map((m: google.maps.Marker) => m.get("markerData"));

      const icon = clusterIconGenerator(clusterMarkerData, markerTypes, 50);

      const index = clustererStyles.findIndex((style) => {
        return style.url === icon[0].url;
      });

      const text = String(markers.length);

      return { index: index + 1, text, title: text };
    },
    [clustererStyles, markerTypes]
  );

  const rerenderMarkers = () => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.panBy(0, 1);
        mapRef.current.panBy(0, -1);
      }, 1000);
    }
  };

  // Display this text if there is an error loading Google Maps from Google-Map-API
  if (loadError) return <p>Error loading maps</p>;

  // Display this text if the map is not yet loaded
  if (!isLoaded) return <p>Loading Maps</p>;

  return (
    <AppSectionErrorBoundary>
      <div className="map-page">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={center}
          zoom={3}
          options={getMapOptions(
            google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
            google.maps.ControlPosition.RIGHT_BOTTOM
          )}
          onLoad={onMapLoad}
          onZoomChanged={rerenderMarkers}
        >
          {!isSwitchingCategory && (
            <MarkerClusterer
              options={{ averageCenter: true }}
              ignoreHidden={true}
              clusterClass={"clusterIcon"}
              calculator={clusterCalculator}
              onClusteringEnd={generateClusterStyles}
              maxZoom={15}
              styles={clustererStyles}
            >
              {(clusterer) => (
                <MapMarkers
                  clusterer={clusterer}
                  systems={filteredSystems}
                  categorySelected={categorySelected}
                  onClick={markerOnClickHandler}
                />
              )}
            </MarkerClusterer>
          )}
          <ToolBar
            panTo={panTo}
            selectedMarkerData={selectedMarkerData}
            setSelectedMarkerId={setSelectedMarkerId}
            categorySelected={categorySelected}
            setCategorySelected={(e: any) => {
              toggleCategory(e);
            }}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            lastSelectedMenuSystemRef={lastSelectedMenuSystemRef}
          />
          <UpdateTimer />
          <ChangeMarkers onChange={rerenderMarkers} />
          <LocateUser panTo={panTo} />
          <WorldZoom panTo={panTo} />
        </GoogleMap>
      </div>
    </AppSectionErrorBoundary>
  );
});
